/**
 * In-app notification sound (Web Audio — no external assets).
 * Classic two-tone “alert” pattern, played twice with a short gap for prominence.
 * Browsers may block audio until a user gesture; call warmNotificationAudio from the bell first.
 */

let sharedCtx: AudioContext | null = null;

function getSharedContext(): AudioContext | null {
  try {
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return null;
    if (!sharedCtx || sharedCtx.state === "closed") {
      sharedCtx = new Ctx();
    }
    return sharedCtx;
  } catch {
    return null;
  }
}

/** Call on bell click / pointer down so socket-driven chimes can play later. */
export function warmNotificationAudio(): void {
  const ctx = getSharedContext();
  if (ctx) void ctx.resume();
}

export function playNotificationChime(): void {
  const ctx = getSharedContext();
  if (!ctx) return;

  void (async () => {
    try {
      await ctx.resume();
    } catch {
      return;
    }
    if (ctx.state === "closed") return;

    const peak = 0.28;
    const master = ctx.createGain();
    master.gain.value = 1;
    master.connect(ctx.destination);

    /** One “ring”: rising two-tone beep (common UI alert feel). */
    const ringAt = (offsetSec: number) => {
      const playTone = (
        startRel: number,
        freq: number,
        duration: number,
        type: OscillatorType
      ) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = type;
        osc.frequency.value = freq;
        const t0 = ctx.currentTime + offsetSec + startRel;
        const t1 = t0 + 0.015;
        const t2 = t0 + duration;
        gain.gain.setValueAtTime(0, t0);
        gain.gain.linearRampToValueAtTime(peak, t1);
        gain.gain.linearRampToValueAtTime(0, t2);
        osc.connect(gain);
        gain.connect(master);
        osc.start(t0);
        osc.stop(t2 + 0.03);
      };
      playTone(0, 880, 0.11, "sine");
      playTone(0.09, 1174.66, 0.12, "sine");
    };

    try {
      ringAt(0.02);
      ringAt(0.48);
    } catch {
      /* ignore */
    }
  })();
}
